import React, { useState } from 'react';
import { Modal } from './ui/Modal';
import { useTermsData, useFormattedEffectiveDate } from '../hooks/useTermsData';
import { TermsErrorBoundary } from './TermsErrorBoundary';
import '../styles/termsContent.css';
import { Button } from './ui';
import { sanitizeHtml } from '../utils/sanitizeHtml';

interface TermsModalProps {
  isOpen: boolean;
  onAgree: () => void;
  onCancel: () => void;
  onClose: () => void;
}

export const TermsModal: React.FC<TermsModalProps> = ({
  isOpen,
  onAgree,
  onCancel,
  onClose,
}) => {
  const { termsData, loading, error } = useTermsData();
  const formattedDate = useFormattedEffectiveDate(termsData);
  const [isProcessing, setIsProcessing] = useState(false);

  // Only show modal when isOpen is true AND terms data is fully loaded
  const shouldShowModal = isOpen && !loading && !!termsData;

  const handleAgree = async () => {
    setIsProcessing(true);
    try {
      // API call will be implemented later
      await onAgree();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    setIsProcessing(true);
    try {
      // API call will be implemented later
      await onCancel();
    } finally {
      setIsProcessing(false);
    }
  };

  // Don't render the modal at all if we're still loading or don't have data
  if (!shouldShowModal || error) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose} // Allow closing via the X button
      title="Terms and Conditions"
      size="xl"
      closeOnBackdrop={false}
    >
      <div className="flex flex-col h-full">
        {/* Terms content */}
        {termsData && (
          <>
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Updated Terms of Use, {formattedDate}
              </h2>
              <p className="text-sm text-gray-700">
                Please review and agree to the Terms of Use to continue using
                the platform.
              </p>
            </div>

            <div className="border border-gray-200 p-4 bg-gray-25 rounded-xl mb-6 max-h-[50vh] overflow-y-auto">
              <TermsErrorBoundary>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {termsData.document.title}
                  </h3>
                  <div>
                    {/* Render HTML content from API — sanitized, see utils/sanitizeHtml */}
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(termsData.content_html),
                      }}
                      className="terms-dynamic-content prose max-w-none"
                      style={{
                        lineHeight: '1.6',
                      }}
                    />

                    {/* Fallback to plain text if HTML content is not available */}
                    {!termsData.content_html && termsData.content && (
                      <div className="whitespace-pre-wrap">
                        <p>{termsData.content}</p>
                      </div>
                    )}
                  </div>
                </div>
              </TermsErrorBoundary>
            </div>

            <div className="text-xs text-gray-700 mb-6">
              Version: {termsData.document.version} | Effective Date:{' '}
              {formattedDate ||
                new Date(
                  termsData.document.effective_date
                ).toLocaleDateString()}{' '}
              | Copyright © 2025 Alō Index All rights reserved.
            </div>

            {/* Action buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isProcessing}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleAgree}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Agree'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
